import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import CartItem from "../Cart/CartItem";
import { useDispatch, useSelector } from "react-redux";
import { getOrderById } from "../../../Redux/Customers/Order/Action";
import AddressCard from "../adreess/AdreessCard";
import { createPayment } from "../../../Redux/Customers/Payment/Action";
import CircularProgress from "@mui/material/CircularProgress"; // Import a loader
import { Loader } from "../../../components/Loader";
import { applyPromoCode } from "../../../Redux/Customers/promo-codes/Action";

const OrderSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get("order_id");
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { order } = useSelector((state) => state);

  const [loadingOrder, setLoadingOrder] = useState(true);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [loadingPromo, setLoadingPromo] = useState(false);
  const [promoCode, setPromoCode] = useState(""); // State for promo code
  const [promoApplied, setPromoApplied] = useState(false); // State to track if promo code is applied
  const [promoError, setPromoError] = useState(""); // State for promo error message
  const [promoSuccess, setPromoSuccess] = useState(""); // State for promo success message

  useEffect(() => {
    const fetchOrder = async () => {
      setLoadingOrder(true);
      await dispatch(getOrderById(orderId));
      setLoadingOrder(false);
    };

    fetchOrder();
  }, [orderId, dispatch]);

  useEffect(() => {
    // Set promo code if it exists in the order
    if (order.order?.promoCode) {
      setPromoCode(order.order.promoCode.code);
      setPromoApplied(true); // Mark promo code as already applied
      setPromoSuccess("Promo code already applied");
    }
  }, [order]);

  const handleCreatePayment = async () => {
    setLoadingPayment(true);
    const data = { orderId: order.order?._id, jwt };
    await dispatch(createPayment(data));
    setLoadingPayment(false);
  };

  const handleApplyPromoCode = async () => {
    setLoadingPromo(true);
    setPromoError(""); // Reset any previous error

    try {
      // Await the dispatch to handle errors properly
      await dispatch(applyPromoCode(orderId, promoCode, jwt));

      setPromoApplied(true);
      setPromoSuccess("Promo code applied successfully");
      await dispatch(getOrderById(orderId)); // Fetch updated order data
    } catch (error) {
      // Catch and set the error message from the action
      setPromoError(error.message); // This will show the error message
    } finally {
      setLoadingPromo(false);
    }
  };


  return (
    <div className="space-y-5">
      {loadingOrder ? (
        <Loader />
      ) : (
        <>
          <div className="p-5 shadow-lg rounded-md border bg-gray-800 border-gray-700">
            <AddressCard address={order.order?.shippingAddress} />
          </div>

          <div className="lg:grid grid-cols-3 relative justify-between">
            <div className="lg:col-span-2">
              <div className="space-y-3">
                {order.order?.orderItems.map((item) => (
                  <CartItem key={item._id} item={item} showButton={false} />
                ))}
              </div>
            </div>

            <div className="sticky top-0 h-[100vh] mt-5 lg:mt-0 ml-5">
              <div className="border p-5 shadow-lg rounded-md bg-gray-800 border-gray-700 text-gray-300">
                <p className="font-bold pb-4 opacity-80">PRICE DETAILS</p>
                <hr className="border-gray-600" />

                <div className="space-y-3 font-semibold">
                  <div className="flex justify-between pt-3 text-gray-300">
                    <span>Price ({order.order?.totalItem} item)</span>
                    <span>₹{order.order?.totalPrice}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Discount</span>
                    <span className="text-green-700">
                      -₹{order.order?.discount || order.order?.discount}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Delivery Charges</span>
                    <span className="text-green-700">Free</span>
                  </div>
                  <hr className="border-gray-600" />
                  <div className="flex justify-between font-bold text-lg text-black text-gray-300">
                    <span>Total Amount</span>
                    <span className="text-green-700">
                      ₹{order.order?.totalDiscountedPrice}
                    </span>
                  </div>
                </div>

                {/* Promo Code Input */}
                <div className="my-4">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Enter Promo Code"
                    className="w-full bg-gray-800 text-white border border-gray-600 p-2 rounded"
                    disabled={promoApplied} // Disable input if promo code is applied
                  />
                  <Button
                    onClick={handleApplyPromoCode}
                    variant="contained"
                    sx={{
                      marginTop: "1rem",
                      backgroundColor: "primary.main",
                      color: "white",
                      '&:hover': {
                        backgroundColor: "primary.dark",
                      },
                    }}
                    disabled={loadingPromo || promoApplied} // Disable if loading or already applied
                  >
                    {loadingPromo ? <CircularProgress size={24} color="inherit" /> : "Apply"}
                  </Button>
                  {promoError && <p className="text-red-500 mt-3">{promoError}</p>}
                  {promoSuccess && <p className="text-green-500 mt-3">{promoSuccess}</p>}
                </div>

                {/* Payment Button */}
                <Button
                  onClick={handleCreatePayment}
                  variant="contained"
                  type="submit"
                  sx={{
                    padding: ".8rem 2rem",
                    marginTop: "2rem",
                    width: "100%",
                    backgroundColor: "primary.main",
                    color: "white",
                    '&:hover': {
                      backgroundColor: "primary.dark",
                    },
                  }}
                  disabled={loadingPayment}
                >
                  {loadingPayment ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Payment"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderSummary;
